import React, {useEffect, useRef, useState} from 'react';
import {Button, Col, Input, PageHeader, Popover, Row, Table,} from 'antd';
import {ReloadOutlined, SearchOutlined} from "@ant-design/icons";
import _ from "lodash";
import {getD365Workers} from "../../api/d365Api";
import moment from "moment";
import {PREFERRED_DATE_FORMAT, PREFERRED_DATETIME_FORMAT} from "../../util/time";
import {getPaginationConfig} from "../../util/showAllPagination";
import {useDynamicTableHeight} from "../../util/useDynamicTableHeight";

const Workers = () => {
  const [loading, setLoading] = useState(false);
  const [workers, setWorkers] = useState([]);
  const [filteredWorkers, setFilteredWorkers] = useState([]);
  const tableContainerRef = useRef(null);
  const tableHeight = useDynamicTableHeight(tableContainerRef);
  const columns = [
    {
      title: 'Employee Info',
      key: 'ID',
      sorter: (a, b) => a.email.localeCompare(b.email),
      render: i => (

        <Popover
          placement="right"
          overlayStyle={{
            width: "45vw"
          }}
          trigger="hover,click,focus"
          content={(
            i.employee ? (
              <>
                <Row>
                  <Col span={8}><b>Greenhouse ID</b></Col>
                  <Col span={16}>{i.employee.greenhouseId ? i.employee.greenhouseId : "-"}</Col>
                </Row>
                <Row>
                  <Col span={8}><b>Name</b></Col>
                  <Col
                    span={16}>{i.employee.lastName ? i.employee.lastName : "-"}, {i.employee.firstName ? i.employee.firstName : "-"}</Col>
                </Row>
                <Row>
                  <Col span={8}><b>Title</b></Col>
                  <Col span={16}>{i.employee.title ? i.employee.title : "-"}</Col>
                </Row>
                <Row>
                  <Col span={8}><b>Level</b></Col>
                  <Col span={16}>{i.employee.level ? i.employee.level : "-"}</Col>
                </Row>
                <Row>
                  <Col span={8}><b>Start Date</b></Col>
                  <Col
                    span={16}>{i.employee.startDate ? moment.utc(i.employee.startDate).format(PREFERRED_DATE_FORMAT) : "-"}</Col>
                </Row>
                <Row>
                  <Col span={8}><b>Termination Date</b></Col>
                  <Col
                    span={16}>{i.employee.termDate ? moment.utc(i.employee.termDate).format(PREFERRED_DATE_FORMAT) : "-"}</Col>
                </Row>
                <Row>
                  <Col span={8}><b>Employment Status</b></Col>
                  <Col span={16}>{i.employee.employmentStatus ? i.employee.employmentStatus : "-"}</Col>
                </Row>
                <Row>
                  <Col span={8}><b>Practice Name</b></Col>
                  <Col span={16}>{i.employee.practiceName ? i.employee.practiceName : "-"}</Col>
                </Row>
                <Row>
                  <Col span={8}><b>Location Name</b></Col>
                  <Col span={16}>{i.employee.locationName ? i.employee.locationName : "-"}</Col>
                </Row>
                <Row>
                  <Col span={8}><b>Capability</b></Col>
                  <Col span={16}>{i.employee.capability ? i.employee.capability : "-"}</Col>
                </Row>
                {(i.employee.manager &&
                  <Row>
                    <Col span={8}><b>Manager</b></Col>
                    <Col
                      span={16}>{i.employee.manager.lastName ? i.employee.manager.lastName : "-"}, {i.employee.manager.firstName ? i.employee.manager.firstName : "-"} - {i.employee.manager.email ? i.employee.manager.email : "-"}</Col>
                  </Row>
                )}
                {(i.employee.careerCoach &&
                  <Row>
                    <Col span={8}><b>Career Coach</b></Col>
                    <Col
                      span={16}>{i.employee.careerCoach.lastName ? i.employee.careerCoach.lastName : "-"}, {i.employee.careerCoach.firstName ? i.employee.careerCoach.firstName : "-"} - {i.employee.careerCoach.email ? i.employee.careerCoach.email : "-"}</Col>
                  </Row>
                )}
              </>
            ) : (
              <>No liftoff employee record found</>
            ))}
        >
          <Button
            danger={!i.employee}
          >{i.email ? i.email : '-'}</Button>
        </Popover>
      ),
    },
    {
      title: 'D365 Entity',
      dataIndex: 'entity',
      width: '8%',
      sorter: (a, b) => a.entity.localeCompare(b.entity),
      render: i => i ? i : '-'
    },
    {
      title: 'Worker ID',
      dataIndex: 'workerId',
      width: '8%',
      sorter: (a, b) => a.workerId.localeCompare(b.workerId),
      render: i => i ? i : '-'
    },
    {
      title: 'Reach ID',
      dataIndex: 'reachId',
      sorter: (a, b) => a.reachId.toString().localeCompare(b.reachId.toString()),
      width: '8%',
      render: i => i ? i : '-'
    },
    {
      title: 'Created At',
      dataIndex: 'CreatedAt',
      sorter: (a, b) => a.CreatedAt.localeCompare(b.CreatedAt),
      width: '200px',
      render: i => i ? moment.utc(i).local().format(PREFERRED_DATETIME_FORMAT) : '-'
    },
  ];

  const filterWorkers = (workers, searchText) => {
    setFilteredWorkers(
      _.chain(workers)
        .filter(
          (u) =>
            searchText === "" ||
            _.includes(_.toLower(u.email), _.toLower(searchText))
        )
        .value()
    );
  };

  const search = (text) => {
    filterWorkers(workers, text);
  };

  const loadWorkers = async () => {
    try {
      setLoading(true);
      const workers = await getD365Workers();
      setWorkers(workers);
      filterWorkers(workers);
    } catch (e) {
      console.error(`Could not get workers`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWorkers();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <Row>
        <Col span={12}>
          <PageHeader
            title="Workers"
            subTitle={`(${filteredWorkers.length} shown)`}
            style={{padding: 0}}
          />
        </Col>
      </Row>

      <Row style={{marginBottom: 10}}>
        <Col span={16} style={{marginRight: 10}}>
          <Input
            style={{maxWidth: 400}}
            placeholder="Search for workers by email"
            prefix={<SearchOutlined/>}
            onChange={(e) => search(e.target.value)}
            allowClear
          />
        </Col>
        <Col flex="auto">
          <Button
            style={{float: 'right'}}
            onClick={() => loadWorkers()}
            loading={loading}
            icon={<ReloadOutlined/>}
          >
            Refresh
          </Button>
        </Col>
      </Row>

      <Table
        ref={tableContainerRef}
        rowKey="ID"
        dataSource={filteredWorkers}
        columns={columns}
        pagination={getPaginationConfig(filteredWorkers.length)}
        size="small"
        loading={loading}
        scroll={{y: tableHeight}}
      />
    </>
  );
};

export {Workers};
